import { duration } from '@play-co/components';
import { AssetGroup } from '@play-co/timestep-core';
import { MovieClip, View, animate } from '@play-co/timestep-core/ui';
import ButtonScaleView, {
  ButtonScaleViewOpts,
} from 'src/lib/ui/components/ButtonScaleView';
import { createWaiter } from 'src/lib/utils';
import { playMovieClip } from '../animation/animation.util';
import {
  HomeButtonLockedOverlay,
  HomeButtonLockedOverlayOpts,
} from './HomeButtonLockedOverlay';
import BadgeNotification from '../badge/BadgeNotification';
import { State } from 'src/replicant/State';
import { PositionType } from '../minigame/match2-timestep/lib/defs/types';
import { SpineView } from '@play-co/timestep-spine';
import { playSpineAnimationSequence } from 'src/lib/ui/utils/spine';
import {
  FeatureButtonLabelPanel,
  FeatureButtonLabelPanelOpts,
} from './FeatureButtonLabel';
import i18n from 'src/lib/i18n/i18n';
import { formatDuration } from 'src/lib/formatDuration';

type FeatureButtonAnimationType =
  | {
      type: 'custom';
    }
  | {
      type: 'movieclip' | 'spine';
      url: string;
      sequence: string[];
      scale?: number;
      offsetX?: number;
      offsetY?: number;
    };

/**
 * FeatureButtonState is a type that represents the state of a FeatureButton.
 * It contains an AssetGroup, that will be loaded when the state is set, and an optional URL and animations to play.
 * If no URL is provided, the MovieClip will be hidden.
 */
type FeatureButtonState = {
  assetGroup: AssetGroup;
  onEnter?: () => void;
} & FeatureButtonAnimationType;

type FeatureButtonOpts = ButtonScaleViewOpts & {
  /**
   * Optional label panel for the button.
   */
  labelPanel?: boolean | Omit<FeatureButtonLabelPanelOpts, 'superview'>;

  /**
   * Optional locked overlay for the button.
   * If specified, it will add a locked overlay to the button
   * and allow for the manual locking and unlocking of the button.
   */
  lockedOverlay?: Omit<HomeButtonLockedOverlayOpts, 'button'>;
};

type FeatureButtonNotificationPositionProps = {
  align: 'left' | 'right';
  offsetX?: number;
  offsetY?: number;
};

type FeatureButtonNotificationProps = {
  /**
   * Checks if the notification should be shown for the given state.
   */
  condition: (user: State, now: number) => boolean;

  /**
   * Optional positioning for the notification badge.
   * Defaults to aligning left without any offset.
   */
  positioning?: FeatureButtonNotificationPositionProps;
};

/**
 * Base class for animated home buttons, that can handle different states and animations.
 * It also includes a built-in auto-update mechanism, that will call the update method every second.
 */
export class FeatureButton<T extends string = 'main'> extends ButtonScaleView {
  public container: View;
  protected movieclip?: MovieClip;
  protected spine?: SpineView;
  protected labelPanel?: FeatureButtonLabelPanel;
  protected notification?: BadgeNotification;
  protected notificationOffset?: PositionType;
  protected lockedOverlay?: HomeButtonLockedOverlay;
  protected waiter?: ReturnType<typeof createWaiter>;
  protected animationStateMap?: Record<T, FeatureButtonState>;
  protected currentAnimationState?: T;

  /**
   * Get the view elements that the lock overlay will apply the desaturate filter onto.
   * To be overridden for supplying any additional elements.
   */
  public getElementsToApplyFilter(): View[] {
    return [this.container];
  }

  public get isLocked() {
    return this.lockedOverlay?.locked ?? false;
  }

  constructor(opts: FeatureButtonOpts) {
    super({
      width: 144,
      height: 160,
      pressedOffsetY: 5,
      ...opts,
    });

    this.container = new View({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      anchorX: this.style.width * 0.5,
      anchorY: this.style.height * 0.5,
      visible: false,
    });

    const { labelPanel, lockedOverlay } = opts;

    // Create label panel if specified.
    if (labelPanel) {
      this.labelPanel = new FeatureButtonLabelPanel({
        superview: this.container,
        ...(typeof labelPanel === 'boolean' ? {} : labelPanel),
      });
    }

    // Create locked overlay if specified that it can be visually locked.
    if (lockedOverlay) {
      this.lockedOverlay = new HomeButtonLockedOverlay({
        button: this,
        ...lockedOverlay,
      });
    }
  }

  /**
   * Starts the button auto-update, that will call the update method every second.
   */
  public autoUpdate() {
    this.waiter?.clear();
    this.waiter = createWaiter();
    this.waiter.wait(this.autoUpdate.bind(this), duration({ seconds: 1 }));
    this.update();
  }

  /**
   * Stops the button auto-update.
   */
  public stopAutoUpdate() {
    this.waiter?.clear();
    this.waiter = undefined;
  }

  /**
   * Update method that will be called every second when the auto-update is running.
   */
  public update() {
    // Button update logic
  }

  /**
   * Sets the current animation state of the button.
   * If the state is different from the current one, it will load the asset group and play the animations.
   * @param state The state to set.
   */
  protected async setAnimationState(state: T) {
    // Get the state configuration from the animation state map
    const stateConfig = this.animationStateMap?.[state];
    if (!stateConfig) return;

    // If the state is the same as the current one, return
    if (this.currentAnimationState === state) return;
    this.currentAnimationState = state;

    // Ensure the asset group is loaded before showing the container
    if (!stateConfig.assetGroup.isLoaded()) {
      this.container.hide();
      await stateConfig.assetGroup.load();
      this.container.show();
    }

    this.resetAnimationViews();

    // Show and play the movieclip if a URL and animations are provided, otherwise hide it
    if (stateConfig.type !== 'custom') {
      switch (stateConfig.type) {
        case 'movieclip':
          this.setMovieClipState(stateConfig);
          break;
        case 'spine':
          this.setSpineState(stateConfig);
          break;
      }
    }

    // Play button pop-out animation when transitioning to a new state
    this.container.updateOpts({ scale: 0.5, visible: true });
    animate(this.container).now({ scale: 1 }, 300, animate.easeOutBack);

    // Call the onEnter callback if provided
    stateConfig?.onEnter?.();
  }

  protected resetAnimationViews() {
    this.movieclip?.hide();
    this.spine?.hide();
  }

  protected setMovieClipState(config: Record<T, FeatureButtonState>[T]) {
    if (config.type !== 'movieclip') return;

    const { url, sequence, scale = 1, offsetX = 0, offsetY = 0 } = config;

    // Create the movieclip if it doesn't exist
    if (!this.movieclip) {
      this.movieclip = new MovieClip({
        superview: this.container,
        zIndex: -1,
      });
    }

    // Update movieclip scale and position
    this.movieclip.updateOpts({
      scale,
      x: this.container.style.width * 0.5 + offsetX,
      y: this.container.style.height * 0.5 + offsetY,
    });

    // Show the movieclip
    this.movieclip?.show();

    // Play the movieclip animations
    playMovieClip(this.movieclip, {
      url,
      animation: sequence,
      loopLast: true,
    });
  }

  protected setSpineState(config: Record<T, FeatureButtonState>[T]) {
    if (config.type !== 'spine') return;

    const { url, sequence, scale = 1, offsetX = 0, offsetY = 0 } = config;

    // Create the spine view if it doesn't exist
    if (!this.spine) {
      this.spine = new SpineView({
        superview: this.container,
        spinePath: url,
        zIndex: -1,
      });
    }

    // TODO: Handle different spine urls ---

    // Update spine position and scale
    this.spine.updateOpts({
      scale,
      x: this.container.style.width * 0.5 + offsetX,
      y: this.container.style.height * 0.5 + offsetY,
    });

    // Show the spine view
    this.spine?.show();

    // Play spine animations
    playSpineAnimationSequence(this.spine, {
      sequence,
      loopLast: true,
    });
  }

  /**
   * Locks the button interactivity and shows the locked overlay.
   */
  public lock() {
    return this.lockedOverlay?.lock();
  }

  /**
   * Unlocks the button interactivity and hides the locked overlay.
   */
  public async unlock(instant = false) {
    return this.lockedOverlay?.unlock(instant);
  }

  /**
   * Updates the label panel background, if the component exists.
   */
  public updateLabelPanel(opts: FeatureButtonLabelPanelOpts) {
    this.labelPanel?.updatePanelOpts(opts);
  }

  /**
   * Sets the label panel text, if the component exists.
   */
  public setLabelText(text: string | number) {
    if (!this.labelPanel) return;
    this.updateLabelPanel({ localeText: () => `${text}` });
  }

  /**
   * Sets the label panel text to a localized copy, if the component exists.
   */
  public setLabelLocaleText(id: string) {
    this.setLabelText(i18n(id));
  }

  /**
   * Sets the label panel text to a formatted duration, if the component exists.
   */
  public setLabelTime(opts: {
    duration: number;
    roundUp?: boolean;
    endText?: string;
  }) {
    const { duration, roundUp = false, endText = 'match2.timer.ended' } = opts;
    if (duration > 0) {
      this.setLabelText(formatDuration(duration, roundUp));
    } else {
      this.setLabelLocaleText(endText);
    }
  }

  /**
   * Add notification functionality to the button.
   */
  public setNotification(opts: FeatureButtonNotificationProps) {
    if (!this.notification) {
      this.notification = new BadgeNotification({
        superview: this.container,
        zIndex: 2,
      });
    }

    // Set the emitter for the notification badge visibility
    this.notification.setEmitter(({ user }, now) => opts.condition(user, now));

    // Position the notification badge
    this.setNotificationPositioning(opts.positioning ?? { align: 'left' });
  }

  /**
   * Sets the positioning of the notification badge.
   */
  public setNotificationPositioning(
    opts: FeatureButtonNotificationPositionProps,
  ) {
    if (!this.notification) return;
    const { align = 'left', offsetX = 0, offsetY = 0 } = opts;
    this.notification.updateOpts({
      x:
        align === 'left'
          ? offsetX
          : this.style.width - this.notification.style.width + offsetX,
      y: 18 + offsetY,
    });
  }
}
